import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

export type MenuItem =
  | { label: string; onClick: () => void; disabled?: boolean; danger?: boolean }
  | { type: 'header'; label: string };

interface OwnProps {
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
}

const StyledMenu = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  left: ${({ $x }) => `${$x}px`};
  top: ${({ $y }) => `${$y}px`};
  z-index: ${({ theme }) => theme.zIndex.modal};
  background: ${({ theme }) => theme.colors.contextMenuBackground};
  border: 1px solid ${({ theme }) => theme.colors.contextMenuBorder};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.contextMenuShadow};
  padding: 4px 0;
  min-width: 160px;
  max-height: 400px;
  overflow-y: auto;
`;

const StyledHeader = styled.div<{ $withDivider: boolean }>`
  padding: 5px 12px 3px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
  ${({ $withDivider, theme }) =>
    $withDivider && `border-top: 1px solid ${theme.colors.borderSubtle}; margin-top: 2px;`};
`;

const StyledItem = styled.button<{ $danger: boolean; $disabled: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 5px 12px;
  background: none;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  color: ${({ $danger, $disabled, theme }) => {
    if ($danger) return theme.colors.dangerText;
    if ($disabled) return theme.colors.borderDashed;
    return theme.colors.textPrimary;
  }};
  font-size: 13px;
  font-weight: ${({ $danger }) => ($danger ? 600 : 400)};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.hoverNeutral};
  }
`;

const isHeader = (item: MenuItem): item is { type: 'header'; label: string } =>
  'type' in item && item.type === 'header';

const ContextMenu = ({ x, y, items, onClose }: OwnProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
        onClose();
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <StyledMenu ref={menuRef} $x={x} $y={y}>
      {items.map((item, i) => {
        if (isHeader(item)) {
          return (
            <StyledHeader key={`h-${item.label}-${i}`} $withDivider={i > 0}>
              {item.label}
            </StyledHeader>
          );
        }

        const handleItemClick = () => {
          item.onClick();
          onClose();
        };

        return (
          <StyledItem
            key={`i-${item.label}-${i}`}
            type='button'
            disabled={item.disabled}
            onClick={handleItemClick}
            $danger={Boolean(item.danger)}
            $disabled={Boolean(item.disabled)}
          >
            {item.label}
          </StyledItem>
        );
      })}
    </StyledMenu>
  );
};

const MemoContextMenu = React.memo(ContextMenu);

export { MemoContextMenu as ContextMenu };
export default MemoContextMenu;
